import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { MaskedLines, FadeUp, EASE } from "@/components/Reveal";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PROJECTS, CATEGORIES } from "@/data/projects";
import useSeo from "@/hooks/useSeo";

export default function Work() {
  useSeo("Campaigns & Work", "Case studies from Vidhan Sabha and Lok Sabha campaigns, Indore Nagar Nigam, government IEC, documentaries and brand launches across Madhya Pradesh.");
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const filtered = filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <div data-testid="work-page" className="mx-auto max-w-[1440px] px-6 pb-24 pt-36 lg:px-12 lg:pt-44">
      <p className="font-mono text-xs font-medium uppercase tracking-[0.25em] text-saffron">Portfolio — Campaigns across India</p>
      <h1 className="mt-5 font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight text-paper sm:text-6xl lg:text-7xl">
        <MaskedLines inView={false} delay={0.1} lines={["Campaigns", "that counted."]} />
      </h1>

      <FadeUp delay={0.3}>
        <div className="mt-12 flex flex-wrap gap-2" data-testid="work-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c.toLowerCase().replace(/[\s&]+/g, "-")}`}
              onClick={() => setFilter(c)}
              className={`rounded-full border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                filter === c
                  ? "border-brand bg-brand text-white"
                  : "border-white/15 text-zinc-400 hover:border-brand/50 hover:text-paper"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </FadeUp>

      <motion.div layout className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
        <AnimatePresence mode="popLayout">
          {filtered.map((p, i) => (
            <motion.article
              layout
              key={p.slug}
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.5, delay: i * 0.05, ease: EASE }}
            >
              <button
                data-testid={`work-card-${p.slug}`}
                onClick={() => setActive(p)}
                className="group relative block w-full overflow-hidden rounded-xl border border-white/10 text-left transition-colors duration-500 hover:border-brand/40"
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={p.img}
                    alt={p.title}
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-6">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-saffron">
                      {p.category} — {p.year}
                    </p>
                    <h3 className="mt-2 font-display text-xl font-bold uppercase tracking-tight text-paper lg:text-2xl">
                      {p.title}
                    </h3>
                    <p className="mt-1 text-sm text-zinc-400">{p.client}</p>
                  </div>
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/20 text-paper opacity-0 transition-all duration-500 group-hover:border-brand group-hover:bg-brand group-hover:opacity-100">
                    <ArrowUpRight className="h-4 w-4" />
                  </span>
                </div>
              </button>
            </motion.article>
          ))}
        </AnimatePresence>
      </motion.div>

      <Dialog open={!!active} onOpenChange={(open) => !open && setActive(null)}>
        <DialogContent
          data-testid="case-study-modal"
          className="max-h-[90vh] max-w-3xl overflow-y-auto border-white/10 bg-coal p-0 text-paper"
        >
          {active && (
            <div>
              <div className="relative">
                <img src={active.img} alt={active.title} className="aspect-[16/9] w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-coal to-transparent" />
              </div>
              <div className="p-8 lg:p-10">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand">
                  {active.category} — {active.year}
                </p>
                <DialogTitle className="mt-3 font-display text-3xl font-bold uppercase tracking-tight text-paper">
                  {active.title}
                </DialogTitle>
                <DialogDescription className="mt-1 text-sm text-zinc-500">{active.client}</DialogDescription>
                <p className="mt-6 text-sm leading-relaxed text-zinc-300 sm:text-base">{active.desc}</p>

                <div className="mt-8 flex flex-wrap gap-2">
                  {active.deliverables.map((d) => (
                    <span key={d} className="rounded-full border border-white/15 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400">
                      {d}
                    </span>
                  ))}
                </div>

                <div className="mt-10 grid grid-cols-3 gap-4 border-t border-white/10 pt-8">
                  {active.metrics.map((m) => (
                    <div key={m.label} data-testid={`metric-${m.label.toLowerCase().replace(/\s+/g, "-")}`}>
                      <p className="font-display text-2xl font-bold text-brand sm:text-3xl">{m.value}</p>
                      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">{m.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
